import { Assets, Texture } from 'pixi.js';
import type { Theme, ThemeAssetEntry } from '../theme';
import { resolveEntryUrl } from './AssetManifestClient';
import { canvasVideoTexture } from './canvasVideo';
import { hevcAlphaPreferredHere, webmAlphaSupportedHere } from './videoAlpha';

/** An alias to load: its URL, and for a transparent WebM the URL of its
 *  HEVC-with-alpha copy, if the theme has one. */
interface LoadEntry {
  alias: string;
  src: string;
  hevc?: string;
}

/** A theme entry as a {@link LoadEntry}, or null when it has no URL yet. */
function toLoadEntry(entry: ThemeAssetEntry): LoadEntry | null {
  const src = resolveEntryUrl(entry);
  if (!src) return null;
  const hevc = entry.hevc ? resolveEntryUrl(entry.hevc) : null;
  return hevc ? { alias: entry.alias, src, hevc } : { alias: entry.alias, src };
}

/** A loaded video asset — both the Pixi {@link Texture} and the
 * underlying {@link HTMLVideoElement}. The texture renders into a
 * Pixi {@code Sprite}; the element is what
 * {@link ../widgets/slot/SlotSymbol SlotSymbol} drives for play /
 * pause / {@code currentTime} reset. The pair stays bound — there's
 * one element per loaded URL, and every Sprite using the texture
 * sees the same playback head. */
export interface LoadedVideo {
  texture: Texture;
  element: HTMLVideoElement;
}

/**
 * Wrapper around Pixi 8's {@link Assets} API. Hosts call
 * {@link AssetLoader#loadTheme} before mounting a scene that uses
 * sprite assets; widgets then look up textures synchronously via
 * {@link AssetLoader#getTexture}.
 *
 * <p><b>Implementation note:</b> Pixi 8's alias-aware
 * {@code Assets.add({alias, src})} + {@code Assets.load(alias)} flow
 * has been unreliable in our Vite-driven dev setup — binaries fetch
 * but the alias→texture mapping doesn't always wire up. Instead we
 * own the alias→URL map ourselves and let Pixi cache textures by URL
 * (its native primary key). The Texture object returned from
 * {@code Assets.load([url, ...])} is also captured into our
 * {@link urlToTexture} map as a belt-and-braces fallback.
 *
 * <p>Audio assets are loaded separately via
 * {@link ../audio/AudioBus AudioBus} (Web Audio, not Pixi textures).
 */
export class AssetLoader {
  /** alias → url. Authoritative. Populated by {@link loadTheme}. */
  private readonly aliasToUrl = new Map<string, string>();
  /** url → Pixi Texture, captured from {@code Assets.load()}'s return
   * object so {@link getTexture} doesn't have to depend on
   * {@code Assets.cache.get()} (which has been flaky). */
  private readonly urlToTexture = new Map<string, Texture>();
  /** url → loaded video pair. Populated by {@link loadTheme} when an
   * entry's URL ends in {@code .webm} / {@code .mp4} / {@code .mov}.
   * Pixi's {@code Assets.load} sometimes coerces video sources into
   * a paused-on-load Texture without the underlying element exposed,
   * so we own the loading path here directly: build an
   * {@link HTMLVideoElement}, await {@code canplaythrough}, wrap with
   * {@code Texture.from(element)}, store the pair. The pair stays
   * bound for the lifetime of the page; revisit if we ship enough
   * themes that lifecycle becomes a concern. */
  private readonly urlToVideo = new Map<string, LoadedVideo>();
  /** Per-URL in-flight load promise. Tracks textures whose
   * {@code Assets.load()} call has been issued but not yet resolved.
   * Lets concurrent {@link loadTheme} calls (React strict-mode
   * double-mount) await the same load instead of racing past an
   * empty texture map and rendering null assets.
   *
   * <p>Once the entry resolves and is stored in {@link urlToTexture},
   * the promise is removed so the map only holds in-flight loads. */
  private readonly inflight = new Map<string, Promise<void>>();
  /** URLs declared as video assets by {@link loadTheme}. Populated
   * when the theme is registered; consumed lazily by {@link getVideo},
   * which constructs the {@link HTMLVideoElement} + {@link Texture}
   * pair on first demand instead of at preload time.
   *
   * <p><b>Why lazy?</b> Eagerly constructing 9-11 detached video
   * elements at boot — even with {@code preload="metadata"} — exposes
   * the page to per-tab video-pipeline bottlenecks: Chrome silently
   * stalls late metadata parses when a stack of paused
   * {@code <video>} elements share the demuxer, leaving the slot
   * scene's preload deadlocked behind a 60s timeout per element. By
   * deferring construction to the moment a celebration actually plays
   * (1 element at a time, on a free decoder slot), we sidestep the
   * whole class of bug. The first win on each symbol pays a small
   * "first-play" latency for metadata parse — ~50ms in the warm case;
   * subsequent wins reuse the cached element and play instantly. */
  private readonly videoUrls = new Set<string>();
  /** alias → URL of its WebM's HEVC-with-alpha copy (a .mov), which
   * {@link getVideo} plays in WebKit, where the WebM's alpha is lost.
   * Fetched only then, and only when the celebration first plays. */
  private readonly aliasToHevcUrl = new Map<string, string>();

  /** Register and preload every visual asset declared by the theme's
   * bundle. Safe to call repeatedly — already-loaded URLs are skipped,
   * in-flight loads from a concurrent call are awaited so the second
   * caller doesn't race past an empty texture map. */
  async loadTheme(theme: Theme): Promise<void> {
    await this.loadEntries(AssetLoader.collectEntries(theme, 'all'));
  }

  /** Load loose entries that belong to no theme (the button plates for a
   *  screen shown before any slot, say), under their aliases. */
  async loadAssets(entries: Record<string, ThemeAssetEntry>): Promise<void> {
    await this.loadEntries(AssetLoader.toLoadEntries(Object.values(entries)));
  }

  /** Load the theme's Royal Road art ({@link Theme} `assets.road`): only
   * for a slot with the road on, so other players never fetch it. */
  async loadThemeRoad(theme: Theme): Promise<void> {
    await this.loadEntries(AssetLoader.toLoadEntries(Object.values(theme.assets?.road?.textures ?? {})));
  }

  /** One road mini-game's art (`theme.assets.road.games[kind]`), fetched
   *  when the game first opens rather than with the road. */
  async loadThemeRoadGame(theme: Theme, kind: string): Promise<void> {
    await this.loadEntries(AssetLoader.toLoadEntries(Object.values(theme.assets?.road?.games?.[kind] ?? {})));
  }

  private static toLoadEntries(entries: readonly ThemeAssetEntry[]): LoadEntry[] {
    return entries.map(toLoadEntry).filter((e): e is LoadEntry => e !== null);
  }

  /** Preload only what a slot needs on screen before its first spin: the
   * reel symbols, background, reel column, frame and HUD icons. Per-symbol
   * win art, win-tier banners, wheel art, other games' textures and bonus
   * (pick'em) art are left
   * to {@link loadTheme}, which callers kick off in the background once
   * this resolves. Lookups before then return null, and every consumer of
   * those groups already falls back (plain win popup, procedural chest).
   * On a cold first visit this is the difference between a few MB and the
   * whole theme before the player can spin. */
  async loadThemeEssentials(
    theme: Theme,
    onProgress?: (loaded: number, total: number) => void,
  ): Promise<void> {
    await this.loadEntries(AssetLoader.collectEntries(theme, 'essentials'), onProgress);
  }

  /** Load loose textures by alias: shared art a scene needs outside any
   *  theme, such as the painted button plates on the realm map's boards. */
  async loadTextures(entries: readonly { alias: string; src: string }[]): Promise<void> {
    await this.loadEntries([...entries]);
  }

  /** The (alias, url) pairs a theme declares, all of them or only the
   * first-spin essentials. */
  private static collectEntries(
    theme: Theme,
    scope: 'all' | 'essentials',
  ): LoadEntry[] {
    if (!theme.assets) return [];
    // Stage 3 — {@link resolveEntryUrl} returns the CDN URL when the
    // manifest knows about {@code entry.path}, and the bundled
    // {@code entry.src} otherwise. So a Stage 2 entry (no path) still
    // loads from the install bundle exactly as before; only opted-in
    // assets fetch from CloudFront.
    // Stage 3 install-excision — resolveEntryUrl can return null
    // when an entry is CDN-only AND the manifest hasn't resolved
    // yet. Skip those entries here (loadTheme is preload-only;
    // the unlock-flow's BiomePackManager handles the actual
    // download). Helper centralises the null-skip + dedupe pattern.
    const allEntries: LoadEntry[] = [];
    const tryPush = (entry: ThemeAssetEntry | undefined): void => {
      const load = entry ? toLoadEntry(entry) : null;
      if (load) allEntries.push(load);
    };
    const all = scope === 'all';
    for (const map of [
      theme.assets.slot,
      all ? theme.assets.slotWin : undefined,
      all ? theme.assets.wheel : undefined,
    ]) {
      if (!map) continue;
      for (const entry of Object.values(map)) tryPush(entry);
    }
    tryPush(theme.assets.scene?.background);
    tryPush(theme.assets.scene?.columnBackground);
    tryPush(theme.assets.scene?.border);
    const uiIcons = theme.assets.scene?.uiIcons;
    if (uiIcons) {
      for (const entry of Object.values(uiIcons)) tryPush(entry);
    }
    if (!all) return allEntries;
    const gameTextures = theme.assets.scene?.gameTextures;
    if (gameTextures) {
      for (const entry of Object.values(gameTextures)) tryPush(entry);
    }
    const winTiers = theme.assets.scene?.winTiers;
    if (winTiers) {
      for (const entry of Object.values(winTiers)) tryPush(entry);
    }
    const pickem = theme.assets.scene?.pickem;
    if (pickem) {
      // Mixed bucket — chest_closed.png + chest_open.png are textures,
      // chest_open_video is a .webm. The {@code isVideoUrl} branch in
      // the load loop below handles the texture/video split via the
      // URL extension.
      for (const entry of Object.values(pickem)) tryPush(entry);
    }
    return allEntries;
  }

  /** Register aliases and load every not-yet-loaded texture in
   * {@code allEntries}; resolves once they're all in (or failed).
   * {@code onProgress} hears (settled, total) as each fetch settles,
   * starting at (0, total); total 0 means everything was cached. */
  private async loadEntries(
    allEntries: LoadEntry[],
    onProgress?: (loaded: number, total: number) => void,
  ): Promise<void> {
    // Register aliases (idempotent) + collect awaitables for every URL
    // that's either pending or not yet started. Video URLs are
    // deferred — we record the alias→url binding and remember which
    // URLs are videos, but no {@code <video>} element is constructed
    // until {@link getVideo} is called for that alias the first time.
    // PNG / spritesheet loads happen here (parallel) and block the
    // returned promise — those are what the slot scene actually needs
    // to render its idle reel grid.
    const awaitables: Promise<void>[] = [];
    for (const entry of allEntries) {
      const alias = entry.alias;
      // An alias keeps the URL it was first registered with. A later call
      // can resolve the same entry to a different URL (the bundled copy
      // before the CDN manifest arrives, the CDN copy after), and fetching
      // it again would download the same art twice while the player waits.
      const src = this.aliasToUrl.get(alias) ?? entry.src;
      if (!this.aliasToUrl.has(alias)) this.aliasToUrl.set(alias, src);
      // A WebM's HEVC copy: noted, never fetched here (see getVideo).
      if (entry.hevc && !this.aliasToHevcUrl.has(alias)) this.aliasToHevcUrl.set(alias, entry.hevc);
      // Already loaded? Done.
      if (this.urlToTexture.has(src) || this.urlToVideo.has(src)) {
        continue;
      }
      // Video URL — note it for lazy load on first {@link getVideo} hit.
      // No element constructed at preload time. See {@link videoUrls}
      // doc for why eager construction was unsafe.
      if (isVideoUrl(src)) {
        this.videoUrls.add(src);
        continue;
      }
      // Already in flight? Await the existing texture load.
      const pending = this.inflight.get(src);
      if (pending) {
        awaitables.push(pending);
        continue;
      }
      // Fresh PNG / spritesheet load via Pixi's Assets API.
      const p: Promise<void> = Assets.load(src).then((res: Texture) => {
        if (res) {
          this.urlToTexture.set(src, res);
        }
      });
      // Wrap to guarantee inflight-map cleanup whether the load
      // succeeds or fails. Swallow individual failures with a warn
      // so a single 404'd PNG doesn't blow up the whole scene's
      // preload — widgets that look up the missing alias get null
      // and fall back to an emoji glyph.
      const tracked = p
        .catch((err: unknown) => {
          console.warn(`[AssetLoader] failed to load '${src}'`, err);
        })
        .finally(() => {
          this.inflight.delete(src);
        });
      this.inflight.set(src, tracked);
      awaitables.push(tracked);
    }
    if (onProgress) {
      const total = awaitables.length;
      let settled = 0;
      onProgress(0, total);
      for (const a of awaitables) void a.then(() => onProgress(++settled, total));
    }
    if (awaitables.length > 0) await Promise.all(awaitables);
  }

  /** Construct an {@link HTMLVideoElement} + Pixi {@link Texture} pair
   * for {@code src} and cache it in {@link urlToVideo}. Called lazily
   * by {@link getVideo} the first time a celebration alias is
   * requested — keeps the page from holding 9-11 detached video
   * elements at boot, which is the configuration that triggers
   * Chrome's video-pipeline stall.
   *
   * <p>The element is appended to a hidden DOM container so Chrome
   * doesn't suspend it as "background video-only media" — the prior
   * detached-element approach generated dozens of {@code AbortError:
   * play() request was interrupted because video-only background
   * media was paused to save power} per session, polluting the
   * console and wasting battery on mobile. Appending to a
   * {@code visibility: hidden} (not {@code display: none}) container
   * keeps the playback graph alive without adding a visible element.
   *
   * <p>{@code throughCanvas} draws the frames through a 2D canvas
   * ({@link canvasVideoTexture}): WebKit loses an HEVC clip's alpha
   * when WebGL takes the frame from the video itself. */
  private constructVideoLazy(src: string, throughCanvas = false): LoadedVideo {
    const element = document.createElement('video');
    element.src = src;
    element.muted = true;          // playsinline + autoplay-muted policy
    element.playsInline = true;
    element.crossOrigin = 'anonymous';
    element.preload = 'auto';      // lazy = full preload OK once requested
    element.loop = false;
    element.autoplay = false;
    AssetLoader.attachToHiddenContainer(element);
    const texture = throughCanvas ? canvasVideoTexture(element) : Texture.from(element);
    const pair: LoadedVideo = { texture, element };
    this.urlToVideo.set(src, pair);
    element.load();
    return pair;
  }

  /** Lazily-created hidden <div> that hosts every win-celebration
   * <video> element. Sized to 1px / hidden via visibility (not
   * display:none — that suspends playback). Only kept around so
   * Chrome treats the elements as live page media instead of
   * "background" subject to power-save pause. */
  private static hiddenVideoHost: HTMLDivElement | null = null;
  private static attachToHiddenContainer(element: HTMLVideoElement): void {
    if (typeof document === 'undefined') return;
    if (!AssetLoader.hiddenVideoHost) {
      const host = document.createElement('div');
      host.setAttribute('aria-hidden', 'true');
      host.style.cssText =
        'position:fixed;left:0;top:0;width:1px;height:1px;' +
        'overflow:hidden;visibility:hidden;pointer-events:none;';
      // Style each child video to match the host so Chrome counts
      // them as composited even though they're invisible.
      document.body.appendChild(host);
      AssetLoader.hiddenVideoHost = host;
    }
    element.style.cssText = 'width:1px;height:1px;';
    AssetLoader.hiddenVideoHost.appendChild(element);
  }

  /** Look up a registered + loaded texture by alias. Returns null when
   * the alias was never registered or the load failed — callers fall
   * back to an alternative renderer (e.g. emoji glyph for slot
   * symbols). */
  getTexture(alias: string): Texture | null {
    const url = this.aliasToUrl.get(alias);
    if (!url) return null;
    return (
      this.urlToTexture.get(url) ??
      (Assets.cache.get(url) as Texture | undefined) ??
      null
    );
  }

  /** Look up a video pair by alias. Constructs the
   * {@link HTMLVideoElement} + {@link Texture} pair on first call —
   * this is the only place a celebration {@code <video>} element ever
   * exists, so we never have more than one element loading at a time
   * unless multiple celebrations actually fire concurrently. Returns
   * null when the alias was never registered or the URL wasn't a
   * video; callers fall back to the spritesheet path or the
   * scale-pulse highlight.
   *
   * <p>Where the browser can't draw a WebM's alpha
   * ({@link webmAlphaSupportedHere}: WebKit) the WebM would show as a
   * black or green box, so the clip's HEVC-with-alpha copy plays
   * instead when the theme has one and the browser decodes HEVC
   * ({@link hevcAlphaPreferredHere}); built lazily, as the WebM is, and
   * drawn through a canvas so WebGL keeps its alpha. Otherwise null
   * there too. */
  getVideo(alias: string): LoadedVideo | null {
    const url = this.aliasToUrl.get(alias);
    if (!url || !this.videoUrls.has(url)) return null;
    if (!isWebmUrl(url) || webmAlphaSupportedHere()) {
      return this.urlToVideo.get(url) ?? this.constructVideoLazy(url);
    }
    const hevc = this.aliasToHevcUrl.get(alias);
    if (!hevc || !hevcAlphaPreferredHere()) return null;
    return this.urlToVideo.get(hevc) ?? this.constructVideoLazy(hevc, true);
  }

  /** Module-shared default loader — used by widgets that can't accept
   * an injected loader (e.g.
   * {@link ../widgets/slot/SlotSymbol SlotSymbol} inside a
   * deeply-nested reel strip). Hosts that only ever construct one
   * Pixi app share state through this; tests construct their own.
   *
   * <p><b>Singleton pinning.</b> Vite + pnpm workspaces can resolve
   * the same source file through two URLs (symlinked
   * {@code node_modules/@goodtime/engine/...} path vs real
   * {@code packages/engine/...} path), giving us TWO copies of this
   * class — and TWO {@code static readonly shared} instances. When
   * {@link ../scenes/SlotDemoScene SlotDemoScene} populates one and
   * {@link ../widgets/slot/SlotSymbol SlotSymbol} reads from the
   * other, the slot silently falls back to emoji glyphs. We pin the
   * singleton on {@code globalThis} via a {@code Symbol.for} key so
   * every module copy ends up reading the same instance. */
  static get shared(): AssetLoader {
    const g = globalThis as unknown as Record<symbol, AssetLoader | undefined>;
    let inst = g[ASSET_LOADER_SHARED_KEY];
    if (!inst) {
      inst = new AssetLoader();
      g[ASSET_LOADER_SHARED_KEY] = inst;
    }
    return inst;
  }
}

/** {@code Symbol.for} returns the same symbol across module copies, so the
 * resulting globalThis slot is shared even when this file is loaded twice
 * by Vite under different URLs. */
const ASSET_LOADER_SHARED_KEY = Symbol.for('@goodtime/engine.AssetLoader.shared');

/** Heuristic for spotting video URLs in the theme bundle. We strip
 * any query string (Vite's {@code ?url} suffix is the common case)
 * before extension-checking. The full set is the codecs Pixi 8 +
 * Chrome / Safari support out of the box. */
function isVideoUrl(src: string): boolean {
  const path = src.split('?')[0]!.toLowerCase();
  return (
    path.endsWith('.webm') ||
    path.endsWith('.mp4') ||
    path.endsWith('.mov') ||
    path.endsWith('.m4v')
  );
}

function isWebmUrl(src: string): boolean {
  return src.split('?')[0]!.toLowerCase().endsWith('.webm');
}
